import { useMemo } from "react";
import { Focus, Video, X } from "lucide-react";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Button } from "@web/components/ui/button.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useArtists } from "@web/features/library/api.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import type { GlobeCountryPanel } from "@web/stores/globe.ts";
import { useGlobeStore } from "@web/stores/globe.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import { useUiStore } from "@web/stores/ui.ts";

/** Country artist list shown in the right sheet. */
export function GlobeCountryPanel({ panel }: { panel: GlobeCountryPanel }) {
  const clear = useGlobeStore((s) => s.clearCountryPanel);
  const requestFocus = useGlobeStore((s) => s.requestFocus);
  const artists = useArtists();
  const openDetail = useOpenDetail();

  const rows = useMemo(() => {
    const idSet = new Set(panel.artistIds);
    const list = (artists.data ?? []).filter((a) => idSet.has(a.id));
    list.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }));
    return list;
  }, [artists.data, panel.artistIds]);

  function focusCountry() {
    if (!panel.countryCode) return;
    requestFocus({ kind: "country", code: panel.countryCode });
    clear();
    useUiStore.getState().setSheetOpen(false);
  }

  function recordCountry() {
    if (!panel.countryCode) return;
    useGlobeShotStore.getState().requestRecord({
      kind: "country",
      code: panel.countryCode,
      artistName: panel.label,
    });
    clear();
    useUiStore.getState().setSheetOpen(false);
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold tracking-tight">{panel.label}</h2>
          <p className="text-xs text-faint">
            {panel.artistIds.length} artist{panel.artistIds.length === 1 ? "" : "s"}
            {panel.playlistName ? ` · in ${panel.playlistName}` : ""}
          </p>
          {panel.countryCode && (
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-accent"
                onClick={focusCountry}
              >
                <Focus className="size-3" />
                Focus on globe
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-accent"
                onClick={recordCountry}
              >
                <Video className="size-3" />
                Record shot
              </button>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Close"
          onClick={() => {
            clear();
            useUiStore.getState().setSheetOpen(false);
          }}
        >
          <X className="size-4" />
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {artists.isLoading && (
          <div className="space-y-2 p-3">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {artists.isError && (
          <p className="p-4 text-sm text-destructive">Failed to load artists.</p>
        )}
        {artists.isSuccess && rows.length === 0 && (
          <p className="p-4 text-sm text-faint">No artists for this filter.</p>
        )}
        <ul className="py-1">
          {rows.map((artist) => (
            <li key={artist.id}>
              <div className="flex items-center gap-1 pr-2 hover:bg-row-hover">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2 text-left"
                  onClick={() => {
                    clear();
                    openDetail(
                      {
                        type: "artist",
                        id: artist.id,
                        label: artist.name ?? artist.id,
                      },
                      { mode: "reset" },
                    );
                  }}
                >
                  <EntityAvatar
                    src={artist.image_url}
                    alt={artist.name ?? artist.id}
                    size="sm"
                    rounded="full"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {artist.name ?? artist.id}
                  </span>
                  <span className="shrink-0 text-xs text-faint">
                    {artist.track_count} track{artist.track_count === 1 ? "" : "s"}
                  </span>
                </button>
                <button
                  type="button"
                  title="Focus on globe"
                  aria-label={`Focus ${artist.name ?? artist.id} on globe`}
                  className="shrink-0 rounded p-1.5 text-faint hover:bg-panel hover:text-accent"
                  onClick={() => {
                    requestFocus({ kind: "artist", id: artist.id });
                    clear();
                    useUiStore.getState().setSheetOpen(false);
                  }}
                >
                  <Focus className="size-3.5" />
                </button>
                <button
                  type="button"
                  title="Record shot"
                  aria-label={`Record shot for ${artist.name ?? artist.id}`}
                  className="shrink-0 rounded p-1.5 text-faint hover:bg-panel hover:text-accent"
                  onClick={() => {
                    useGlobeShotStore.getState().requestRecord({
                      kind: "artist",
                      id: artist.id,
                    });
                    clear();
                    useUiStore.getState().setSheetOpen(false);
                  }}
                >
                  <Video className="size-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
